<template>
  <div
    class="popup-holder popup-confirm"
    :style="{ display: show ? 'flex' : 'none' }"
  >
    <div
      ref="dialog"
      class="popup-wrap"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <div class="popup-head">
        <h2 :id="titleId">{{ title }}</h2>
        <a
          href="#"
          class="popup-close icon-close"
          aria-label="Close"
          @click.prevent="handleCancel"
        ></a>
      </div>
      <div class="popup-body">
        <!--
          Callers presenting untrusted values should use the slot and render
          them as separate elements. Passing them inside `message` means one
          text node, where an attacker-supplied newline can forge a second
          labelled line — which is exactly why this dialog does NOT set
          white-space: pre-line.
        -->
        <slot>
          <p class="confirm-message">{{ message }}</p>
        </slot>
      </div>
      <div class="popup-foot">
        <div class="btn-holder">
          <button
            ref="cancelButton"
            class="btn btn-secondary"
            @click="handleCancel"
            :disabled="isLoading"
          >
            {{ cancelText }}
          </button>
          <button
            class="btn"
            :class="variant === 'danger' ? 'btn-danger' : 'btn-primary'"
            @click="handleConfirm"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="btn-spinner"></span>
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trapTab } from "@/utils/focusTrap";

let nextDialogId = 0;

export default {
  name: "ConfirmDialog",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Confirm",
    },
    message: {
      type: String,
      default: "Are you sure?",
    },
    confirmText: {
      type: String,
      default: "Delete",
    },
    cancelText: {
      type: String,
      default: "Cancel",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "danger",
      validator: (value) => ["danger", "primary"].includes(value),
    },
  },

  emits: ["confirm", "cancel"],

  data() {
    return {
      titleId: `confirm-dialog-title-${++nextDialogId}`,
      restoreFocusTo: null,
    };
  },

  watch: {
    show(newVal) {
      if (newVal) {
        document.addEventListener("keydown", this.handleEscape);
        document.addEventListener("keydown", this.trapFocus);
        // Land keyboard and screen-reader users inside the dialog, on the
        // safe choice, and hand focus back when it closes.
        this.restoreFocusTo = document.activeElement;
        this.$nextTick(() => this.$refs.cancelButton?.focus());
      } else {
        document.removeEventListener("keydown", this.handleEscape);
        document.removeEventListener("keydown", this.trapFocus);
        this.restoreFocusTo?.focus?.();
        this.restoreFocusTo = null;
      }
    },
  },

  methods: {
    handleEscape(event) {
      if (event.key === "Escape" && !this.isLoading) {
        this.handleCancel();
      }
    },

    // Tab and Shift+Tab stay inside the dialog while it's shown.
    trapFocus(event) {
      trapTab(event, this.$refs.dialog);
    },

    handleConfirm() {
      if (!this.isLoading) {
        this.$emit("confirm");
      }
    },

    handleCancel() {
      if (!this.isLoading) {
        this.$emit("cancel");
      }
    },
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscape);
    document.removeEventListener("keydown", this.trapFocus);
  },
};
</script>

<style scoped>
.popup-confirm .popup-wrap {
  max-width: 400px;
}

.confirm-message {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  margin: 0;
}

.btn-holder {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background: #e8e8e8;
}

.btn-primary {
  background: #f16622;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #e55a1c;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
